(function () {
  "use strict";

  var DEFAULTS = {
    maxProducts: 8,
    timerSeconds: 10,
    successThreshold: 25,
    perfectThreshold: 10,
    enableSound: true,
    showProductLink: true,
    rewardThreshold: 10000,
    rewardMessage: ""
  };

  var WORLD = {
    width: 360,
    height: 210,
    minViewportWidth: 740,
    minViewportHeight: 560,
    pickupRadius: 9,
    playerRadius: 11,
    playerSpeed: 92,
    perfectBonusRate: 0.1
  };

  var STORAGE_KEY = "mosWorldBestScore";
  var STATES = {
    boot: "boot",
    intro: "intro",
    playing: "playing",
    encounter: "encounter",
    result: "result",
    levelComplete: "level_complete",
    paused: "paused",
    error: "error"
  };

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function money(pence) {
    var amount = Number(pence || 0) / 100;
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP"
    }).format(amount);
  }

  function escapeHtml(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function shuffle(items) {
    var copy = items.slice();
    for (var index = copy.length - 1; index > 0; index -= 1) {
      var swap = Math.floor(Math.random() * (index + 1));
      var temp = copy[index];
      copy[index] = copy[swap];
      copy[swap] = temp;
    }
    return copy;
  }

  function parseJson(root, selector, fallback) {
    var node = root.querySelector(selector);
    if (!node) return fallback;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return fallback;
    }
  }

  function normaliseProduct(product) {
    if (!product || !product.id || !product.title || !product.featured_image) return null;
    var price = parseInt(product.price, 10);
    if (!Number.isFinite(price) || price <= 0) return null;
    return {
      id: String(product.id),
      handle: String(product.handle || ""),
      url: String(product.url || "#"),
      title: String(product.title || "Mystery gear"),
      image: String(product.featured_image || ""),
      imageAlt: String(product.image_alt || product.title || "Product image"),
      pricePence: price,
      vendor: String(product.vendor || ""),
      productType: String(product.product_type || ""),
      mosays: String(product.mosays || "").trim()
    };
  }

  function getFocusable(container) {
    return Array.prototype.slice.call(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    );
  }

  function MosWorldGame(root) {
    this.root = root;
    this.config = Object.assign({}, DEFAULTS, parseJson(root, "[data-mos-config]", {}));
    this.rawProducts = parseJson(root, "[data-mos-products]", []);
    this.products = this.rawProducts.map(normaliseProduct).filter(Boolean);
    this.canvas = root.querySelector("[data-mos-canvas]");
    this.ctx = this.canvas.getContext("2d");
    this.stage = root.querySelector("[data-mos-stage]");
    this.modal = root.querySelector("[data-mos-modal]");
    this.dialog = root.querySelector("[data-mos-dialog]");
    this.modalContent = root.querySelector("[data-mos-modal-content]");
    this.scoreNode = root.querySelector("[data-mos-score]");
    this.bagNode = root.querySelector("[data-mos-bag]");
    this.bestNode = root.querySelector("[data-mos-best]");
    this.state = STATES.boot;
    this.previousState = STATES.intro;
    this.keys = {};
    this.animationFrame = null;
    this.lastFrameTime = 0;
    this.timerId = null;
    this.timerStartedAt = 0;
    this.timerDuration = Math.max(1, Number(this.config.timerSeconds || DEFAULTS.timerSeconds)) * 1000;
    this.audioContext = null;
    this.lastFocused = null;
    this.currentProduct = null;
    this.pickups = [];
    this.player = { x: 48, y: 110, vx: 0, vy: 0 };
    this.run = {};
    this.boundLoop = this.loop.bind(this);
    this.boundKeyDown = this.onKeyDown.bind(this);
    this.boundKeyUp = this.onKeyUp.bind(this);
    this.boundResize = this.onResize.bind(this);

    this.boot();
  }

  MosWorldGame.prototype.boot = function () {
    this.setState(STATES.boot);
    this.bindEvents();
    this.drawStaticScene();
    this.updateHud();

    if (!this.products.length) {
      this.showError("Mo found no eligible gear. Choose a collection with available products that have images and prices.");
      return;
    }

    if (!this.hasPlayableResolution()) {
      this.showError("This screen is a touch too snug for the bazaar. Try a desktop-sized view so the whole level fits without scrolling.");
      return;
    }

    this.bestScore = this.getBestScore();
    this.updateHud();
    this.setState(STATES.intro);
    this.showScreen("intro");
  };

  MosWorldGame.prototype.bindEvents = function () {
    var startButtons = this.root.querySelectorAll("[data-mos-start], [data-mos-replay]");
    var resume = this.root.querySelector("[data-mos-resume]");
    var pause = this.root.querySelector("[data-mos-pause]");

    Array.prototype.forEach.call(startButtons, function (button) {
      button.addEventListener("click", this.startRun.bind(this));
    }, this);

    if (resume) resume.addEventListener("click", this.resume.bind(this));
    if (pause) pause.addEventListener("click", this.pause.bind(this));

    window.addEventListener("keydown", this.boundKeyDown);
    window.addEventListener("keyup", this.boundKeyUp);
    window.addEventListener("resize", this.boundResize);

    this.modal.addEventListener("keydown", this.trapModalFocus.bind(this));
  };

  MosWorldGame.prototype.hasPlayableResolution = function () {
    return window.innerWidth >= WORLD.minViewportWidth && window.innerHeight >= WORLD.minViewportHeight;
  };

  MosWorldGame.prototype.onResize = function () {
    if (this.state !== STATES.playing && this.state !== STATES.paused) return;
    if (!this.hasPlayableResolution()) {
      this.pause();
      this.showError("Mo has paused because the game no longer fits. Widen the window and hit replay when ready.");
    }
  };

  MosWorldGame.prototype.setState = function (nextState) {
    this.state = nextState;
    this.root.dataset.mosState = nextState;
  };

  MosWorldGame.prototype.showScreen = function (name) {
    var screens = this.root.querySelectorAll("[data-mos-screen]");
    Array.prototype.forEach.call(screens, function (screen) {
      var active = screen.getAttribute("data-mos-screen") === name;
      screen.hidden = !active;
      screen.classList.toggle("is-visible", active);
    });
  };

  MosWorldGame.prototype.showError = function (message) {
    this.stopTimer();
    this.stopLoop();
    this.hideModal();
    this.setState(STATES.error);
    var messageNode = this.root.querySelector("[data-mos-error-message]");
    if (messageNode) messageNode.textContent = message;
    this.showScreen("error");
  };

  MosWorldGame.prototype.startRun = function () {
    if (!this.hasPlayableResolution()) {
      this.showError("Mo needs a little more room. Please use a desktop-sized view to play the full level.");
      return;
    }

    this.unlockSound();
    this.bestScore = this.getBestScore();
    this.selectedProducts = shuffle(this.products).slice(0, clamp(parseInt(this.config.maxProducts, 10) || DEFAULTS.maxProducts, 1, 24));
    this.run = {
      score: 0,
      bagged: [],
      totalValue: 0,
      guesses: [],
      encountered: 0
    };
    this.player.x = 48;
    this.player.y = 110;
    this.keys = {};
    this.pickups = this.selectedProducts.map(this.createPickup.bind(this));
    this.currentProduct = null;
    this.hideModal();
    this.showScreen("");
    this.setState(STATES.playing);
    this.updateHud();
    this.lastFrameTime = performance.now();
    this.loop(this.lastFrameTime);
    this.canvas.focus();
    this.playTone(440, 0.05);
  };

  MosWorldGame.prototype.createPickup = function (product, index) {
    var angle = Math.random() * Math.PI * 2;
    var speed = 18 + Math.random() * 18;
    return {
      product: product,
      x: 92 + ((index * 41) % 220),
      y: 42 + ((index * 29) % 130),
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      bob: Math.random() * 100,
      kind: this.pickupKind(product)
    };
  };

  MosWorldGame.prototype.pickupKind = function (product) {
    var haystack = (product.productType + " " + product.title).toLowerCase();
    if (haystack.indexOf("guitar") !== -1) return "guitar";
    if (haystack.indexOf("drum") !== -1 || haystack.indexOf("cymbal") !== -1) return "drum";
    if (haystack.indexOf("key") !== -1 || haystack.indexOf("synth") !== -1 || haystack.indexOf("piano") !== -1) return "keys";
    if (haystack.indexOf("amp") !== -1 || haystack.indexOf("speaker") !== -1) return "amp";
    if (haystack.indexOf("mic") !== -1) return "mic";
    return "guitar";
  };

  MosWorldGame.prototype.loop = function (time) {
    if (this.state !== STATES.playing) return;
    var delta = Math.min(0.05, (time - this.lastFrameTime) / 1000 || 0);
    this.lastFrameTime = time;
    this.update(delta);
    this.draw();
    this.animationFrame = window.requestAnimationFrame(this.boundLoop);
  };

  MosWorldGame.prototype.stopLoop = function () {
    if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame);
    this.animationFrame = null;
  };

  MosWorldGame.prototype.update = function (delta) {
    var dx = 0;
    var dy = 0;
    if (this.keys.ArrowLeft || this.keys.KeyA) dx -= 1;
    if (this.keys.ArrowRight || this.keys.KeyD) dx += 1;
    if (this.keys.ArrowUp || this.keys.KeyW) dy -= 1;
    if (this.keys.ArrowDown || this.keys.KeyS) dy += 1;

    if (dx || dy) {
      var length = Math.sqrt(dx * dx + dy * dy);
      this.player.x += (dx / length) * WORLD.playerSpeed * delta;
      this.player.y += (dy / length) * WORLD.playerSpeed * delta;
    }

    this.player.x = clamp(this.player.x, WORLD.playerRadius, WORLD.width - WORLD.playerRadius);
    this.player.y = clamp(this.player.y, WORLD.playerRadius + 20, WORLD.height - WORLD.playerRadius);

    for (var index = 0; index < this.pickups.length; index += 1) {
      var pickup = this.pickups[index];
      pickup.x += pickup.vx * delta;
      pickup.y += pickup.vy * delta;
      pickup.bob += delta * 6;

      if (pickup.x < WORLD.pickupRadius || pickup.x > WORLD.width - WORLD.pickupRadius) pickup.vx *= -1;
      if (pickup.y < 32 || pickup.y > WORLD.height - WORLD.pickupRadius) pickup.vy *= -1;
      pickup.x = clamp(pickup.x, WORLD.pickupRadius, WORLD.width - WORLD.pickupRadius);
      pickup.y = clamp(pickup.y, 32, WORLD.height - WORLD.pickupRadius);

      var distance = Math.hypot(this.player.x - pickup.x, this.player.y - pickup.y);
      if (distance < WORLD.playerRadius + WORLD.pickupRadius) {
        this.collectPickup(index);
        return;
      }
    }
  };

  MosWorldGame.prototype.collectPickup = function (index) {
    var pickup = this.pickups.splice(index, 1)[0];
    this.currentProduct = pickup.product;
    this.run.encountered += 1;
    this.stopLoop();
    this.setState(STATES.encounter);
    this.playTone(660, 0.06);
    this.showEncounter(pickup.product);
  };

  MosWorldGame.prototype.showEncounter = function (product) {
    this.lastFocused = document.activeElement;
    this.modalContent.innerHTML = [
      '<form class="mos-world-game__encounter" data-mos-guess-form>',
      '<img class="mos-world-game__product-image" src="' + escapeHtml(product.image) + '" alt="' + escapeHtml(product.imageAlt) + '" loading="eager">',
      '<div>',
      '<p class="mos-world-game__kicker">Name Mo\'s price</p>',
      '<h3 id="' + this.modalTitleId() + '">' + escapeHtml(product.title) + "</h3>",
      '<p>' + escapeHtml(product.vendor || "Mystery vendor") + (product.productType ? " - " + escapeHtml(product.productType) : "") + "</p>",
      '<div class="mos-world-game__timer" aria-live="polite">',
      '<div class="mos-world-game__timer-track"><div class="mos-world-game__timer-fill" data-mos-timer-fill></div></div>',
      '<strong class="mos-world-game__timer-count" data-mos-timer-count>' + escapeHtml(this.config.timerSeconds) + "</strong>",
      "</div>",
      '<div class="mos-world-game__field">',
      '<label for="MosWorldGuess-' + escapeHtml(this.config.sectionId) + '">Your guess in GBP</label>',
      '<div class="mos-world-game__price-wrap"><span aria-hidden="true">&pound;</span><input id="MosWorldGuess-' + escapeHtml(this.config.sectionId) + '" data-mos-guess-input type="number" min="0" step="0.01" inputmode="decimal" autocomplete="off" required></div>',
      "</div>",
      '<div class="mos-world-game__actions"><button class="mos-world-game__button" type="submit">Make the deal</button></div>',
      "</div>",
      "</form>"
    ].join("");

    this.modal.hidden = false;
    this.dialog.focus();

    var form = this.modalContent.querySelector("[data-mos-guess-form]");
    var input = this.modalContent.querySelector("[data-mos-guess-input]");
    form.addEventListener("submit", function (event) {
      event.preventDefault();
      this.resolveEncounter(this.readGuess(input), false);
    }.bind(this));

    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        input.focus();
        this.startEncounterTimer();
      }.bind(this));
    }.bind(this));
  };

  MosWorldGame.prototype.modalTitleId = function () {
    return "MosWorldModalTitle-" + String(this.config.sectionId || "game");
  };

  MosWorldGame.prototype.startEncounterTimer = function () {
    var fill = this.modalContent.querySelector("[data-mos-timer-fill]");
    var count = this.modalContent.querySelector("[data-mos-timer-count]");
    this.stopTimer();
    this.timerDuration = Math.max(1, Number(this.config.timerSeconds || DEFAULTS.timerSeconds)) * 1000;
    this.timerStartedAt = performance.now();

    this.timerId = window.setInterval(function () {
      var elapsed = performance.now() - this.timerStartedAt;
      var remaining = Math.max(0, this.timerDuration - elapsed);
      var ratio = remaining / this.timerDuration;
      if (fill) fill.style.transform = "scaleX(" + ratio + ")";
      if (count) count.textContent = String(Math.ceil(remaining / 1000));
      if (remaining <= 0) {
        this.resolveEncounter(null, true);
      }
    }.bind(this), 100);
  };

  MosWorldGame.prototype.stopTimer = function () {
    if (this.timerId) window.clearInterval(this.timerId);
    this.timerId = null;
  };

  MosWorldGame.prototype.readGuess = function (input) {
    var value = parseFloat(input.value);
    if (!Number.isFinite(value) || value < 0) return null;
    return Math.round(value * 100);
  };

  MosWorldGame.prototype.resolveEncounter = function (guessPence, timedOut) {
    if (this.state !== STATES.encounter) return;
    this.stopTimer();

    var product = this.currentProduct;
    var actual = product.pricePence;
    var errorPercent = guessPence == null ? 100 : Math.abs(guessPence - actual) / actual * 100;
    var perfectThreshold = Number(this.config.perfectThreshold || DEFAULTS.perfectThreshold);
    var successThreshold = Math.max(perfectThreshold, Number(this.config.successThreshold || DEFAULTS.successThreshold));
    var verdict = {
      label: "No deal",
      tone: "Mo clutches the ledger. That one slips back into the dunes.",
      bagged: false,
      bonus: 0
    };

    if (!timedOut && errorPercent <= perfectThreshold) {
      verdict = {
        label: "Perfect deal",
        tone: "Mo's moustache points north. That is proper trading.",
        bagged: true,
        bonus: Math.max(100, Math.round(actual * WORLD.perfectBonusRate))
      };
    } else if (!timedOut && errorPercent <= successThreshold) {
      verdict = {
        label: "Near miss",
        tone: "Close enough to make the sphinx raise an eyebrow, but not a bagger.",
        bagged: false,
        bonus: 0
      };
    } else if (timedOut) {
      verdict.tone = "Time ran out while Mo was polishing a tuning peg.";
    }

    if (verdict.bagged) {
      this.run.bagged.push(product);
      this.run.totalValue += actual;
      this.run.score += actual + verdict.bonus;
      this.playTone(880, 0.08);
    } else {
      this.playTone(180, 0.08);
    }

    this.run.guesses.push({
      product: product,
      guessPence: guessPence,
      actualPence: actual,
      errorPercent: errorPercent,
      timedOut: timedOut,
      verdict: verdict.label
    });

    this.updateHud();
    this.setState(STATES.result);
    this.showResult(product, guessPence, verdict, errorPercent, timedOut);
  };

  MosWorldGame.prototype.showResult = function (product, guessPence, verdict, errorPercent, timedOut) {
    var continueLabel = this.pickups.length ? "Back to the bazaar" : "See the tally";
    var mosays = product.mosays || "Mo says: every price has a story, and some of them wear tiny boots.";
    var cta = "";

    if (this.config.showProductLink && product.url && product.url !== "#") {
      cta = '<a class="mos-world-game__button mos-world-game__button--ghost" href="' + escapeHtml(product.url) + '" target="_blank" rel="noopener">View the gear</a>';
    }

    this.modalContent.innerHTML = [
      '<div class="mos-world-game__result">',
      '<p class="mos-world-game__kicker">Deal verdict</p>',
      '<h3 id="' + this.modalTitleId() + '">' + escapeHtml(verdict.label) + "</h3>",
      '<p>' + escapeHtml(verdict.tone) + "</p>",
      '<dl class="mos-world-game__result-list">',
      "<div><dt>Product</dt><dd>" + escapeHtml(product.title) + "</dd></div>",
      "<div><dt>Guessed price</dt><dd>" + escapeHtml(timedOut || guessPence == null ? "No guess" : money(guessPence)) + "</dd></div>",
      "<div><dt>Actual price</dt><dd>" + escapeHtml(money(product.pricePence)) + "</dd></div>",
      "<div><dt>Accuracy gap</dt><dd>" + escapeHtml(Math.round(errorPercent)) + "%</dd></div>",
      "</dl>",
      '<div class="mos-world-game__mosays">' + escapeHtml(mosays) + "</div>",
      '<div class="mos-world-game__actions">',
      cta,
      '<button class="mos-world-game__button" type="button" data-mos-continue>' + continueLabel + "</button>",
      "</div>",
      "</div>"
    ].join("");

    var button = this.modalContent.querySelector("[data-mos-continue]");
    button.addEventListener("click", this.afterResult.bind(this));
    button.focus();
  };

  MosWorldGame.prototype.afterResult = function () {
    this.hideModal();
    if (!this.pickups.length) {
      this.completeLevel();
      return;
    }
    this.setState(STATES.playing);
    this.lastFrameTime = performance.now();
    this.loop(this.lastFrameTime);
    this.canvas.focus();
  };

  MosWorldGame.prototype.completeLevel = function () {
    this.stopLoop();
    this.stopTimer();
    this.setState(STATES.levelComplete);
    this.hideModal();

    if (this.run.score > this.getBestScore()) {
      try {
        window.localStorage.setItem(STORAGE_KEY, String(this.run.score));
      } catch (error) {
        // Local storage can be blocked in privacy modes; the run still completes.
      }
    }

    this.bestScore = this.getBestScore();
    this.updateHud();

    var accuracy = this.calculateAccuracy();
    this.root.querySelector("[data-mos-final-score]").textContent = String(this.run.score);
    this.root.querySelector("[data-mos-final-bag]").textContent = String(this.run.bagged.length) + " / " + String(this.selectedProducts.length);
    this.root.querySelector("[data-mos-final-value]").textContent = money(this.run.totalValue);
    this.root.querySelector("[data-mos-final-accuracy]").textContent = String(accuracy) + "%";
    this.root.querySelector("[data-mos-rank]").textContent = this.rankTitle(accuracy);

    var reward = this.root.querySelector("[data-mos-reward]");
    var threshold = Number(this.config.rewardThreshold || 0);
    if (reward && threshold > 0 && this.run.score >= threshold && this.config.rewardMessage) {
      reward.hidden = false;
      reward.textContent = this.config.rewardMessage;
    } else if (reward) {
      reward.hidden = true;
      reward.textContent = "";
    }

    this.showScreen("complete");
  };

  MosWorldGame.prototype.calculateAccuracy = function () {
    if (!this.run.guesses.length) return 0;
    var totalError = this.run.guesses.reduce(function (sum, guess) {
      return sum + Math.min(100, guess.errorPercent);
    }, 0);
    return clamp(Math.round(100 - totalError / this.run.guesses.length), 0, 100);
  };

  MosWorldGame.prototype.rankTitle = function (accuracy) {
    var bagRate = this.selectedProducts.length ? this.run.bagged.length / this.selectedProducts.length : 0;
    if (bagRate === 1 && accuracy >= 90) return "Pharaoh of Fair Deals";
    if (bagRate >= 0.7) return "Grand Vizier of Gear";
    if (bagRate >= 0.4) return "Bazaar Bargain Boss";
    if (accuracy >= 60) return "Sandy Calculator";
    return "Apprentice Case Carrier";
  };

  MosWorldGame.prototype.pause = function () {
    if (this.state !== STATES.playing) return;
    this.previousState = this.state;
    this.stopLoop();
    this.setState(STATES.paused);
    this.showScreen("paused");
  };

  MosWorldGame.prototype.resume = function () {
    if (this.state !== STATES.paused) return;
    this.showScreen("");
    this.setState(STATES.playing);
    this.lastFrameTime = performance.now();
    this.loop(this.lastFrameTime);
    this.canvas.focus();
  };

  MosWorldGame.prototype.hideModal = function () {
    this.modal.hidden = true;
    this.modalContent.innerHTML = "";
    if (this.lastFocused && typeof this.lastFocused.focus === "function") {
      this.lastFocused.focus();
    }
  };

  MosWorldGame.prototype.updateHud = function () {
    var score = this.run.score || 0;
    var bag = this.run.bagged ? this.run.bagged.length : 0;
    this.scoreNode.textContent = String(score);
    this.bagNode.textContent = String(bag);
    this.bestNode.textContent = String(this.bestScore || this.getBestScore());
  };

  MosWorldGame.prototype.getBestScore = function () {
    try {
      return parseInt(window.localStorage.getItem(STORAGE_KEY) || "0", 10) || 0;
    } catch (error) {
      return 0;
    }
  };

  MosWorldGame.prototype.onKeyDown = function (event) {
    if (event.code === "KeyP" && this.state === STATES.playing) {
      event.preventDefault();
      this.pause();
      return;
    }
    if (event.code === "Escape") {
      if (this.state === STATES.playing) this.pause();
      return;
    }
    if (this.state !== STATES.playing) return;
    if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "KeyA", "KeyD", "KeyW", "KeyS"].indexOf(event.code) !== -1) {
      event.preventDefault();
      this.keys[event.code] = true;
    }
  };

  MosWorldGame.prototype.onKeyUp = function (event) {
    delete this.keys[event.code];
  };

  MosWorldGame.prototype.trapModalFocus = function (event) {
    if (event.key !== "Tab") return;
    var focusable = getFocusable(this.modal);
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  MosWorldGame.prototype.unlockSound = function () {
    if (!this.config.enableSound || this.audioContext) return;
    var AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    this.audioContext = new AudioContext();
  };

  MosWorldGame.prototype.playTone = function (frequency, duration) {
    if (!this.config.enableSound || !this.audioContext) return;
    var oscillator = this.audioContext.createOscillator();
    var gain = this.audioContext.createGain();
    oscillator.type = "square";
    oscillator.frequency.value = frequency;
    gain.gain.value = 0.035;
    oscillator.connect(gain);
    gain.connect(this.audioContext.destination);
    oscillator.start();
    oscillator.stop(this.audioContext.currentTime + duration);
  };

  MosWorldGame.prototype.drawStaticScene = function () {
    this.draw();
  };

  MosWorldGame.prototype.draw = function () {
    var ctx = this.ctx;
    ctx.save();
    ctx.scale(2, 2);
    this.drawEgypt(ctx);

    for (var i = 0; i < this.pickups.length; i += 1) {
      this.drawPickup(ctx, this.pickups[i]);
    }

    this.drawMo(ctx);
    ctx.restore();
  };

  MosWorldGame.prototype.pixel = function (ctx, x, y, w, h, color) {
    ctx.fillStyle = color;
    ctx.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
  };

  MosWorldGame.prototype.drawEgypt = function (ctx) {
    var p = this.pixel.bind(this, ctx);
    var theme = String(this.config.worldTheme || "Egypt").toLowerCase();
    var night = theme.indexOf("night") !== -1;
    var bazaar = theme.indexOf("bazaar") !== -1;
    var sand = night ? "#8a613d" : (bazaar ? "#cf8f4d" : "#d8a35d");
    var sky = night ? "#182842" : "#58b8d7";
    var horizon = night ? "#f6c453" : "#ffe7a1";

    p(0, 0, WORLD.width, WORLD.height, sand);
    p(0, 0, WORLD.width, 30, sky);
    p(0, 30, WORLD.width, 8, horizon);
    p(18, 18, 24, 6, "#fff0b3");
    p(282, 10, 20, 20, "#fce176");

    for (var s = 0; s < 26; s += 1) {
      p((s * 37) % WORLD.width, 44 + ((s * 17) % 145), 6, 2, "#bf7f3b");
    }

    this.drawPyramid(ctx, 38, 150, 66, 50);
    this.drawPyramid(ctx, 86, 154, 48, 36);
    this.drawPyramid(ctx, 255, 145, 78, 58);
    this.drawPalm(ctx, 310, 74);
    this.drawPalm(ctx, 28, 78);
    p(0, 190, WORLD.width, 20, "#b97538");
    p(0, 194, WORLD.width, 3, "#e2b269");
    p(148, 57, 46, 8, "#c88a46");
    p(154, 49, 34, 8, "#e0b36c");
    p(164, 41, 14, 8, "#c88a46");
  };

  MosWorldGame.prototype.drawPyramid = function (ctx, x, y, width, height) {
    var p = this.pixel.bind(this, ctx);
    for (var row = 0; row < height; row += 4) {
      var inset = Math.round(row * width / (height * 2));
      p(x + inset, y - row, width - inset * 2, 4, row % 8 === 0 ? "#e8bf74" : "#d99d55");
    }
    p(x + width / 2, y - height, 4, height, "#f5d891");
    p(x + width / 2 - 4, y - 13, 8, 13, "#5e3a24");
  };

  MosWorldGame.prototype.drawPalm = function (ctx, x, y) {
    var p = this.pixel.bind(this, ctx);
    p(x, y, 6, 40, "#7b4b2a");
    p(x - 3, y + 8, 6, 8, "#9d6234");
    p(x - 14, y - 4, 34, 7, "#2b8b57");
    p(x - 8, y - 14, 24, 7, "#3ba767");
    p(x + 2, y - 8, 28, 7, "#27794d");
    p(x - 20, y + 4, 24, 7, "#3ba767");
  };

  MosWorldGame.prototype.drawMo = function (ctx) {
    var p = this.pixel.bind(this, ctx);
    var x = Math.round(this.player.x);
    var y = Math.round(this.player.y);
    p(x - 6, y - 16, 12, 7, "#f4c177");
    p(x - 8, y - 11, 16, 10, "#f4c177");
    p(x - 10, y - 6, 20, 4, "#2b1610");
    p(x - 3, y - 8, 2, 2, "#0a1020");
    p(x + 3, y - 8, 2, 2, "#0a1020");
    p(x - 8, y, 16, 14, "#3269c8");
    p(x - 13, y + 2, 5, 12, "#f4c177");
    p(x + 8, y + 2, 5, 12, "#f4c177");
    p(x - 7, y + 14, 5, 9, "#252a3a");
    p(x + 2, y + 14, 5, 9, "#252a3a");
    p(x - 9, y - 20, 18, 5, "#b33b2f");
    p(x - 4, y - 25, 8, 5, "#b33b2f");
  };

  MosWorldGame.prototype.drawPickup = function (ctx, pickup) {
    var offset = Math.sin(pickup.bob) * 2;
    var x = Math.round(pickup.x);
    var y = Math.round(pickup.y + offset);
    if (pickup.kind === "drum") this.drawDrum(ctx, x, y);
    else if (pickup.kind === "keys") this.drawKeys(ctx, x, y);
    else if (pickup.kind === "amp") this.drawAmp(ctx, x, y);
    else if (pickup.kind === "mic") this.drawMic(ctx, x, y);
    else this.drawGuitar(ctx, x, y);
  };

  MosWorldGame.prototype.drawGuitar = function (ctx, x, y) {
    var p = this.pixel.bind(this, ctx);
    p(x - 3, y - 13, 6, 21, "#613a1f");
    p(x - 10, y + 3, 20, 13, "#d75b35");
    p(x - 6, y + 6, 12, 7, "#f7c453");
    p(x - 1, y - 15, 2, 26, "#fff7c7");
  };

  MosWorldGame.prototype.drawDrum = function (ctx, x, y) {
    var p = this.pixel.bind(this, ctx);
    p(x - 10, y - 8, 20, 16, "#2e6bd1");
    p(x - 12, y - 10, 24, 5, "#fff7c7");
    p(x - 12, y + 5, 24, 5, "#fff7c7");
    p(x - 7, y - 3, 14, 6, "#73e9ff");
  };

  MosWorldGame.prototype.drawKeys = function (ctx, x, y) {
    var p = this.pixel.bind(this, ctx);
    p(x - 13, y - 7, 26, 14, "#222b3d");
    p(x - 10, y - 4, 20, 8, "#fff7c7");
    p(x - 6, y - 4, 2, 7, "#111");
    p(x, y - 4, 2, 7, "#111");
    p(x + 6, y - 4, 2, 7, "#111");
  };

  MosWorldGame.prototype.drawAmp = function (ctx, x, y) {
    var p = this.pixel.bind(this, ctx);
    p(x - 12, y - 12, 24, 24, "#293549");
    p(x - 9, y - 9, 18, 18, "#111827");
    p(x - 5, y - 5, 10, 10, "#6b7c9c");
    p(x - 8, y - 14, 16, 3, "#fff7c7");
  };

  MosWorldGame.prototype.drawMic = function (ctx, x, y) {
    var p = this.pixel.bind(this, ctx);
    p(x - 5, y - 13, 10, 14, "#cad6e5");
    p(x - 7, y - 10, 14, 4, "#6b7c9c");
    p(x - 1, y, 2, 14, "#f7c453");
    p(x - 8, y + 12, 16, 3, "#f7c453");
  };

  function init() {
    var roots = document.querySelectorAll("[data-mos-world-game]");
    Array.prototype.forEach.call(roots, function (root) {
      if (!root.mosWorldGame) root.mosWorldGame = new MosWorldGame(root);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
